/**
 * P3 (Entwurf 29a) — die Anfragenliste als ARBEITSLISTE: vier Gruppen „wer ist dran", der Stand
 * als ganzer Satz, genau EINE Aktion je Zeile.
 *
 * Bauplan `docs/KONZEPT-ANFRAGEN-WER-IST-DRAN.md` § 1 (was 29a verlangt), § 6 (Zuordnung
 * Stand → Gruppe → Aktion), § 7 (was der Umbau NICHT tut). Zustand/Beweise:
 * `docs/LOOP-ANFRAGEN-WER-IST-DRAN.md`, Kriterien P3.1–P3.4.
 *
 * ── Diese Datei formuliert NICHTS ────────────────────────────────────────────────────────────
 * Gruppen-Überschriften (`ANZEIGE_GRUPPEN_TITEL`), ihre Untertitel (`gruppenUntertitel`), der
 * Stand-Satz (`stand.satz`) und die Beschriftung der einen Aktion (`stand.aktion.text`) kommen
 * ausnahmslos aus `@maestro/anfrage-stand`. Genau dafür wohnt die Regel in `packages/` und nicht
 * in `apps/api` (§ 5): zwei Listen mit derselben Behauptung laufen auseinander. Hier entsteht
 * ausschließlich Anordnung — kein Satzbestandteil, keine zweite Aktionsliste.
 *
 * ── Kein Satz ohne Beleg, auch in der Anzeige (P0.3) ────────────────────────────────────────
 * Eine Zeile OHNE `stand` bekommt hier keinen erfundenen Ersatz. Sie steht unter einer eigenen,
 * ausdrücklich benannten Überschrift ohne Satz und ohne Aktion. Der Grund steht im
 * Zwischenkopf: eine nicht ausgelieferte Tatsache ist keine beobachtete Abwesenheit — dieselbe
 * Unterscheidung, die die Regel zwischen `aufrufe: 0` und `aufrufe: null` trifft.
 *
 * ── Ein Status-Badge, aber nur eines (P3.2 fortgeschrieben, 03.09.2026) ────────────────────────
 * Ursprünglich stand hier „kein Status-Chip": der alte Farbcode las `inquiries.status`
 * (u. a. `'quoted'`) und behauptete bei 11 von 17 Storia-Zeilen fälschlich, ein Angebot sei raus
 * — deshalb wurde er entfernt, nicht ergänzt. Domenico (via WhatsApp/Antoine) vermisste danach
 * genau diese Information beim schnellen Scannen: der Versand-Hinweis steckt im `StandSatz`
 * mitunter nur im unbetonten `rest`, sobald `ohneAntwortTeil` den `kopf` übernimmt. Die Antwort
 * ist NICHT die Rückkehr zur alten Quelle, sondern ein schmales `StatusPill` neben „Kam rein",
 * gespeist aus `stand.angebotVersendetAm` (`@maestro/anfrage-stand`, seinerseits aus
 * `events.offer_sent_at` — derselben Tatsache, die `stand.satz` bereits korrekt füllt). Rot
 * bleibt weiterhin echten Fehlern (§ 6, Schlusssatz) — sichtbar allein über
 * `dringlichkeit: "fehler"`; dieses Badge ist immer `tone: "warn"`, nie rot, und ersetzt den
 * Satz nicht, es ergänzt ihn nur ums schnelle Scannen.
 */
import { useState } from "react";
import {
  ANZEIGE_GRUPPEN_REIHENFOLGE,
  ANZEIGE_GRUPPEN_TITEL,
  ANZEIGE_GRUPPE_VON,
  GRUPPEN_REIHENFOLGE,
  gruppenUntertitel,
  type AnfrageStand,
  type AnzeigeGruppe,
  type StandAktion,
  type StandGruppe,
  type StandRegel,
} from "@maestro/anfrage-stand";
import type { Inquiry } from "./types";
import { formatDate, money, timeAgo } from "./format";
import { HerkunftPille } from "./HerkunftPille";
import { NotizZaehler } from "./NotizZeile";
import { GastAntwortChip, UngelesenPunkt, ungeleseneGastAntwort } from "./GastAntwortZeichen";
import { GrundPilleInline } from "./GrundPille";
import { grundPille, zustellZeile } from "../lib/grund-pille";
import {
  Card,
  EmptyState,
  Icon,
  KeineAngabe,
  LISTEN_KOPF_ZELLE,
  ListFooter,
  SortierKopf,
  StatusPill,
  ZEILEN_AKTION,
  ZeilenLink,
} from "./components";
import { useListSort, type ListSortZustand, type SortSpalte } from "../hooks/useListSort";
import { listenMonat } from "../lib/anfrage-angaben";
import { A_LA_CARTE_KLEIN } from "../lib/a-la-carte";

/**
 * Was eingeklappt startet (§ 1, Antoine: „mit alle anzeigen"). Es sind dieselben Zeilen wie
 * vorher — sie stehen seit 32a nur unter EINER Überschrift „Entschieden" (§ 12.5).
 */
const EINGEKLAPPT: ReadonlySet<AnzeigeGruppe> = new Set<AnzeigeGruppe>(["entschieden"]);

export interface AnfrageZeile {
  anfrage: Inquiry;
  stand: AnfrageStand;
}

export interface GruppenAufteilung {
  gruppen: { gruppe: StandGruppe; zeilen: AnfrageZeile[] }[];
  /** Zeilen, für die kein Stand vorliegt — sie werden gezeigt, aber nichts über sie behauptet. */
  ohneStand: Inquiry[];
  /** Wie viele Anfragen zählen — die EINE Zahl der Kopfzeile UND des Seitenleisten-Badges (§ 1, Punkt 4; P10.6). */
  wartetAufDich: number;
}

/**
 * Vergleicht zwei Zeilen für die Anzeige-Reihenfolge: primär `createdAt` absteigend (neueste
 * zuerst), sekundär `stand.sortierung` (Dringlichkeit, aus der Regel — wird hier NICHT
 * nachgerechnet, siehe `teileInGruppen`) als Tie-Breaker bei auf die Millisekunde gleichem
 * `createdAt`.
 *
 * Antoine, 19.08.2026: eine dringlichkeitsbasierte Sortierung schiebt eine brandneue Anfrage
 * (geringe Dringlichkeit) auf Seite 4 und lässt sie im Kunden-Workflow untergehen — die
 * 24-Stunden-SLA für neue Leads verlangt zwingend eine klassische, chronologische Inbox: neueste
 * zuerst, unabhängig von der Dringlichkeitsstufe.
 *
 * Einzige Instanz dieses Vergleichs — beide Sortierstellen unten rufen dieselbe Funktion auf,
 * damit keine zweite, abweichende Ordnung entsteht.
 */
function vergleicheZeilen(a: AnfrageZeile, b: AnfrageZeile): number {
  /*
   * Antoine, 06.10.2026: „neue müssen immer zuoberst stehen“. Die frühere D2-Vorrangregel für heiße
   * Vorgänge (`heiss_anrufen` zuerst) schob eine Anfrage von vor 2 Std. unter Karten vom 07.09. —
   * sie bestimmt die Reihenfolge nicht mehr; die Karte zeigt „Jetzt anrufen“ weiterhin.
   */
  const chronologisch = new Date(b.anfrage.createdAt).getTime() - new Date(a.anfrage.createdAt).getTime();
  if (chronologisch !== 0) return chronologisch;
  return a.stand.sortierung - b.stand.sortierung;
}

/**
 * LISTENSTANDARD (Punkt 95, Antoine 23.09.2026: „Spaltenköpfe ersetzen die Sortierauswahl“).
 * Die Auswahl vom 21.09.2026 (Neueste / Dringlichkeit / Nach Wert) ist in den Spaltenköpfen
 * aufgegangen — keine ihrer drei Ordnungen fällt weg:
 *
 *  - **Neueste zuerst** ist weiter die VORGABE (Sortierung „aus“). Die Begründung vom 19.08.2026
 *    (24-Stunden-Regel) gilt unverändert; `anfragen-sortierung.test.tsx` prüft sie.
 *  - **Dringlichkeit** = Spalte „Dringlichkeit“ (bis 24.09.2026 „Stand“, Punkt 107): sortiert wird nach `stand.sortierung`, dem Schlüssel der
 *    Regel — hier wird nichts nachgerechnet, die Regel bleibt die eine Wahrheit.
 *  - **Nach Wert** = Spalte „Wert“ (`amountTotalCents`, server-autoritativ, nur verglichen). Ohne
 *    Betrag ist der Wert leer und steht in beiden Richtungen unten — „kein Angebot“ ist kein
 *    hoher Wert.
 *
 * Sortiert wird INNERHALB jeder Gruppe, die Gruppen („wer ist dran“) bleiben die Ordnung der
 * Liste. Bei Gleichstand bleibt die chronologische Vorgabe stehen (stabile Sortierung).
 */
export const ANFRAGEN_SPALTEN: SortSpalte<AnfrageZeile>[] = [
  { key: "kunde", label: "Kunde", type: "text", accessor: (z) => z.anfrage.customerName },
  { key: "eingang", label: "Kam rein", type: "date", accessor: (z) => z.anfrage.createdAt },
  { key: "stand", label: "Dringlichkeit", type: "number", accessor: (z) => z.stand.sortierung },
  { key: "wert", label: "Wert", type: "money", accessor: (z) => z.anfrage.amountTotalCents ?? null },
];

/**
 * Dieselben Ordnungen als Menü — für die Ansichten OHNE Spaltenköpfe (Kanban, Telefon; Brief § 9).
 * Es ist derselbe Zustand (`useListSort("anfragen", …)`), nur anders bedient.
 */
export const ANFRAGEN_SORTIERUNG = [
  { label: "Neueste zuerst", key: null },
  { label: "Älteste zuerst", key: "eingang", dir: "asc" },
  { label: "Dringlichkeit", key: "stand", dir: "asc" },
  { label: "Nach Wert", key: "wert", dir: "desc" },
  { label: "Kunde A–Z", key: "kunde", dir: "asc" },
] as const;

/** Die Sortierung der Anfragen — Liste, Kanban und Telefon lesen denselben Zustand. */
export function useAnfragenSortierung(): ListSortZustand<AnfrageZeile> {
  return useListSort("anfragen", ANFRAGEN_SPALTEN);
}

/**
 * Teilt die Liste in die vier Gruppen der Regel. Reine Funktion, damit die Kopfzeile und die
 * Abschnitte dieselbe Zählung benutzen und nicht zwei.
 *
 * Innerhalb jeder Gruppe steht die neueste Anfrage oben, streng chronologisch nach `createdAt`
 * absteigend (`vergleicheZeilen`) — Antoine, 19.08.2026: die 24-Stunden-SLA für neue Leads
 * verlangt eine klassische Inbox, keine Sortierung nach Dringlichkeit, die frische Anfragen unter
 * alten liegen lässt. `stand.sortierung` entscheidet nur noch bei auf die Millisekunde gleichem
 * `createdAt`. Eine gewählte Sortierung legt sich danach darüber (`ANFRAGEN_SPALTEN`).
 */
export function teileInGruppen(anfragen: Inquiry[]): GruppenAufteilung {
  const eimer = new Map<StandGruppe, AnfrageZeile[]>();
  for (const g of GRUPPEN_REIHENFOLGE) eimer.set(g, []);
  const ohneStand: Inquiry[] = [];

  for (const anfrage of anfragen) {
    const stand = anfrage.stand;
    if (!stand || !eimer.has(stand.gruppe)) {
      ohneStand.push(anfrage);
      continue;
    }
    eimer.get(stand.gruppe)!.push({ anfrage, stand });
  }

  for (const zeilen of eimer.values()) {
    zeilen.sort(vergleicheZeilen);
  }

  return {
    gruppen: GRUPPEN_REIHENFOLGE.map((gruppe) => ({ gruppe, zeilen: eimer.get(gruppe) ?? [] })),
    ohneStand,
    wartetAufDich: (eimer.get("du_bist_dran") ?? []).filter((z) => WARTET_MIT_REGEL.has(z.stand.regel)).length,
  };
}

/**
 * Fix 03.09.2026 (Domenico via WhatsApp/Antoine: die Anfragen-Glocke ist „dauerhaft an") — die
 * Teil-Regeln von „Du bist dran", die noch in `wartetAufDich` zählen.
 *
 * Vorher zählten alle sieben Teil-Regeln der Gruppe (nur `wird_generiert` war schon ausgenommen,
 * s. Kommentar dazu unten) — darunter auch `angebot_unversendet`/`angebot_leer`/
 * `zustellung_fehlgeschlagen`: Vorgänge, an denen bereits ein Entwurf angefasst wurde. Live gegen
 * die Storia-Daten (Neon `soft-lake-86506456`, 03.09.2026) waren das 29 von 55 Anfragen, obwohl
 * nur eine Handvoll tatsächlich frisch war — der Rest waren wochenalte unversendete Entwürfe und
 * Testdaten-Altlasten. Eine Zahl, die dauerhaft zweistellig ist, benachrichtigt nicht mehr, sie
 * ist nur noch Hintergrundrauschen — und genau das trägt seit P10.6 sowohl das Seitenleisten-Badge
 * als auch die Kopfzeile dieser Liste (EIN Zähler, siehe unten).
 *
 * Diese Menge lässt deshalb die drei „schon angefasst"-Teil-Regeln weg und behält nur die drei,
 * bei denen noch gar kein Angebot begonnen wurde: fehlende Gästezahl, fehlende Nachricht, oder
 * vollständig aber noch nicht angefasst. Die ausgelassenen Zeilen verschwinden NICHT aus der
 * Liste — sie stehen weiterhin sichtbar unter „Du bist dran" mit ihrem eigenen Satz und ihrer
 * Aktion (z.B. „Angebot senden"); nur die aggregierte Zahl zählt sie nicht mehr mit.
 *
 * AP6 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md): die vierte, ehemals hier gelistete Regel
 * `import_ohne_stand` (§ 6, ehem. Zeile 6 "kein Stand/Import") ist entfallen — seit Migration 412
 * hat jede Anfrage sofort einen, ggf. leeren, Stamm-Vorgang; der Fall war seither unerreichbar.
 */
const WARTET_MIT_REGEL: ReadonlySet<StandRegel> = new Set<StandRegel>([
  "kein_angebot_gaeste_fehlen",
  "kein_angebot_rueckfrage",
  "kein_angebot_vollstaendig",
]);

/**
 * P10.6 (32a, § 12.2 Punkt 2) — **die eine Zählwahrheit**: wie viele Anfragen auf den Betrieb
 * warten. Speist SOWOHL das Seitenleisten-Badge (`AppShell.tsx`) ALS AUCH die Kopfzeile dieser
 * Liste — absichtlich EIN Zähler, kein zweiter fürs Badge und ein anderer für die Kopfzeile.
 *
 * Warum diese Funktion überhaupt existiert: das Seitenleisten-Badge hat bis P10.6 `status ===
 * "new"` gezählt, die Kopfzeile dagegen die Gruppe „Du bist dran". Zwei Zählungen derselben Menge
 * liefen auseinander — genau der Widerspruch, den der Entwurf benennt (§ 12.1: Badge `6` · Reiter
 * „6 offen" · Kopfzeile „bei 17 wartet etwas auf dich"). Ein zweiter, für die Glocke enger
 * gefasster Zähler wäre derselbe Fehler in neuer Form (belegt durch `anfragen-zaehlwahrheit.test.tsx`,
 * P10.6) — deshalb wird stattdessen diese EINE Funktion enger (s. `WARTET_MIT_REGEL` oben), nicht
 * eine zweite eingeführt.
 *
 * Ohne Liste (noch nicht geladen) ist die Antwort 0 und nicht etwa geraten — Badge und Kopfzeile
 * bleiben dann einfach weg.
 */
export function zaehleWartetAufDich(anfragen: Inquiry[] | null | undefined): number {
  return anfragen ? teileInGruppen(anfragen).wartetAufDich : 0;
}

/** Ein Abschnitt auf dem Bildschirm — eine Überschrift, ein Untertitel, die Zeilen darunter. */
export interface AnzeigeAbschnitt {
  gruppe: AnzeigeGruppe;
  titel: string;
  /** „6 Anfragen · 1 gewonnen · 5 verloren oder abgesagt" — gezählt hier, formuliert in der Regel. */
  untertitel: string;
  zeilen: AnfrageZeile[];
}

/**
 * Legt „Gebucht" und „Abgelehnt oder abgesagt" unter die eine Überschrift „Entschieden"
 * (32a, § 12.5). Die Regel-Gruppen bleiben, was sie sind — hier wird nur zusammengelegt, was
 * nebeneinander stünde.
 *
 * Zwei Dinge kommen ausdrücklich NICHT von hier: die Überschrift und der Untertitel. Beide
 * stammen aus `@maestro/anfrage-stand`; diese Datei zählt nur, was sie ohnehin schon in Händen
 * hält. Sortiert wird die zusammengelegte Gruppe erneut mit `vergleicheZeilen` — demselben
 * Vergleich wie oben (`createdAt` absteigend, dann Dringlichkeit als Tie-Breaker), damit nicht
 * zwei Ordnungen entstehen und die chronologische Reihenfolge über die Gruppengrenze hinweg
 * erhalten bleibt.
 */
export function fasseFuerAnzeigeZusammen(gruppen: { gruppe: StandGruppe; zeilen: AnfrageZeile[] }[]): AnzeigeAbschnitt[] {
  const anzahl: Record<StandGruppe, number> = {
    du_bist_dran: 0,
    kunde_ist_dran: 0,
    gebucht: 0,
    abgelehnt: 0,
  };
  const eimer = new Map<AnzeigeGruppe, AnfrageZeile[]>();
  for (const g of ANZEIGE_GRUPPEN_REIHENFOLGE) eimer.set(g, []);

  for (const { gruppe, zeilen } of gruppen) {
    anzahl[gruppe] = zeilen.length;
    eimer.get(ANZEIGE_GRUPPE_VON[gruppe])!.push(...zeilen);
  }

  for (const zeilen of eimer.values()) {
    zeilen.sort(vergleicheZeilen);
  }

  return ANZEIGE_GRUPPEN_REIHENFOLGE.map((gruppe) => ({
    gruppe,
    titel: ANZEIGE_GRUPPEN_TITEL[gruppe],
    untertitel: gruppenUntertitel(gruppe, anzahl),
    zeilen: eimer.get(gruppe) ?? [],
  }));
}

/**
 * „60 Gäste · 05.06.2027" (§ 1, Punkt 5). Fehlt eine der beiden Angaben, fehlt sie auch hier —
 * es steht kein Platzhalter und kein „unbekannt" in der Zeile.
 */
export function eckdatenZeile(
  i: Pick<Inquiry, "guests" | "eventDate"> & Partial<Pick<Inquiry, "wunschMonat" | "terminOffen" | "message" | "createdAt">>,
): string | null {
  const teile: string[] = [];
  if (i.guests != null && i.guests > 0) teile.push(`${i.guests} Gäste`);
  if (i.eventDate) {
    const datum = formatDate(i.eventDate);
    if (datum !== "–") teile.push(datum);
  } else {
    // Punkt 130: nur ein Monat bekannt → „Dez“ / „Jan 2027“.
    const monat = listenMonat(i);
    if (monat) teile.push(monat);
  }
  return teile.length > 0 ? teile.join(" · ") : null;
}

/**
 * Exportiert seit dem Kanban-Redesign (08.09.2026): die kanban-spezifische Karte
 * (`AnfragenKanban.tsx`) baut ihre eigene Anordnung, verwendet dafuer aber diese Bausteine statt
 * sie ein zweites Mal zu formulieren — keine zweite Initialen-/Stand-Satz-/Aktions-Logik.
 */
export function initialen(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/** Rot bleibt echten Fehlern (§ 6). Warten ist nicht rot, auch wenn es lange dauert. */
function satzFarbe(stand: AnfrageStand): string {
  return stand.dringlichkeit === "fehler"
    ? "text-error dark:text-error"
    : "text-on-surface dark:text-inverse-on-surface";
}

/**
 * P10.5 (32a, § 12.4) — der Termin-Chip ÜBER dem Satz: amber, mit Punkt.
 *
 * Der Wortlaut kommt aus `stand.chip` und damit aus der Regel. Hier wird keine Frist nachgerechnet
 * und keine Bedingung nachgebaut — stünde die Rechnung auch hier, gäbe es zwei Rechnungen
 * derselben Frist, und Chip und Satz liefen irgendwann auseinander.
 *
 * Der Chip ist bewusst **nicht** eingefärbt nach `dringlichkeit`: er ist eine zeitliche Auskunft
 * und keine vierte Dringlichkeitsstufe. Rot bleibt echten Fehlern — die trägt der Satz.
 */
function TerminChip({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <span className="mb-1 inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-full bg-[#F0EAE0] px-2 py-0.5 text-[10.5px] font-semibold text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
      <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />
      {text}
    </span>
  );
}

/**
 * Das Badge aus dem P3.2-Nachtrag oben: sichtbar nur, wenn `stand.angebotVersendetAm` gesetzt ist
 * (`@maestro/anfrage-stand`, aus `events.offer_sent_at` — dieselbe Tatsache wie im Stand-Satz).
 * Label und Ton sind dieselben wie in der Veranstaltungen-Liste (`EVENT_STATUS.offer_sent`,
 * `ui/format.ts`), damit beide Listen dieselbe Aussage im selben visuellen Vokabular zeigen —
 * das Datum kommt unverändert aus der Regel, wird hier nicht neu berechnet.
 */
export function AngebotVersendetBadge({ stand }: { stand: AnfrageStand }) {
  if (!stand.angebotVersendetAm) return null;
  return <StatusPill meta={{ label: `Angebot gesendet · ${stand.angebotVersendetAm}`, tone: "warn" }} />;
}

/**
 * P10.5 (32a, § 12.3) — Chip, fetter Kopf, magerer Rest. Genau eine Stelle für beide Fassungen,
 * damit Breitbild und Telefon nicht zweimal dasselbe anders zusammensetzen.
 *
 * Wo der erste Satz endet, entscheidet die Regel (`stand.kopf` / `stand.rest`) und nicht diese
 * Datei: ein deutsches Kurzdatum endet selbst auf einem Punkt, aus der Zeichenkette wäre die
 * Grenze also nur zu raten. Zusammen ergeben Kopf und Rest wörtlich `stand.satz` — es kommt kein
 * Wort dazu und es fällt keines weg.
 */
export function StandSatz({ stand }: { stand: AnfrageStand }) {
  return (
    <div className="flex flex-col">
      <TerminChip text={stand.chip} />
      <p className={`font-body-sm text-[12.5px] leading-relaxed ${satzFarbe(stand)}`}>
        <strong className="font-semibold">{stand.kopf}</strong>
        {stand.rest ? ` ${stand.rest}` : ""}
      </p>
    </div>
  );
}

interface AktionsKnopfProps {
  stand: AnfrageStand;
  onAktion: (aktion: StandAktion) => void;
  breit?: boolean;
}

/**
 * Die EINE Aktion. Sie ruft nur auf, was es schon gibt (§ 7): das Ziel steht in `aktion.ziel`
 * und stammt aus der Regel — die Zeile sendet nichts, erzeugt nichts und rechnet nichts.
 * Ohne Aktion steht hier nichts zu tun (§ 6, Zeile 9) und auch kein Ersatzknopf.
 *
 * P10.5: **gefüllt für „Angebot überprüfen", Umriss für alle übrigen.** Der Entwurf hebt genau diese
 * eine Handlung hervor, weil sie der Übergang ist, auf den die Liste hinarbeitet. Unterschieden
 * wird am SCHLÜSSEL der Regel, nicht an der Beschriftung — der Text ist Anzeige, der Schlüssel
 * ist der Vertrag (dieselbe Unterscheidung wie in `fuehreAktionAus`).
 *
 * § 6, Zeile 14: `aktion === null` heißt nicht mehr automatisch „nichts zu tun". Läuft gerade der
 * automatische Entwurf (`stand.regel === "wird_generiert"`), steht hier statt „Nichts zu tun" ein
 * Spinner — dieselbe Bildsprache wie im Vollbild-Fortschritts-Modal
 * (`pages/inquiries.tsx`, Icon `progress_activity` + `animate-spin`). Antoine, wörtlich zur alten
 * Zeile „✨ Rückfrage schreiben" an dieser Stelle: „Das macht keinen Sinn." — der Automat arbeitet
 * bereits, ein Knopf zum Nachfragen wäre hier eine falsche Aufforderung.
 */
export function AktionsKnopf({ stand, onAktion, breit }: AktionsKnopfProps) {
  if (stand.regel === "wird_generiert") {
    return (
      <span className={`inline-flex items-center gap-1.5 text-[12px] font-medium text-primary ${breit ? "w-full justify-center" : ""}`}>
        <Icon name="progress_activity" className="animate-spin text-[14px]" />
        Angebot wird erstellt
      </span>
    );
  }
  const aktion = stand.aktion;
  if (!aktion) {
    return <span className="text-[12px] text-on-surface-variant/60">Nichts zu tun</span>;
  }
  const gefuellt = aktion.schluessel === "angebot_senden";
  /*
   * Buchungsstrecke D2: „Jetzt anrufen“ ist ein `tel:`-Link — ein echtes `<a>`, damit Telefon und
   * Rechner (Softphone) den Anruf selbst öffnen. Kein Router-Ziel, deshalb nicht über `onAktion`.
   */
  if (aktion.ziel.startsWith("tel:")) {
    return (
      <a
        href={aktion.ziel}
        data-testid="aktion-jetzt-anrufen"
        onClick={(e) => e.stopPropagation()}
        className={`${ZEILEN_AKTION} inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-lg border border-transparent bg-primary px-3 py-1.5 text-[12px] font-semibold text-on-primary motion-safe:transition-colors hover:bg-[#a8380a] ${breit ? "w-full" : ""}`}
      >
        <Icon name="call" className="text-[14px]" />
        {aktion.text}
      </a>
    );
  }
  return (
    // Einheit E: `ZEILEN_AKTION` haelt den Knopf ueber der Overlay-Link-Flaeche der Zeile
    // (`ZeilenLink`), sonst laege er darunter und waere nicht mehr klickbar. `stopPropagation`
    // bleibt: die Zeile selbst hat zwar keinen `onClick` mehr, aber ein Klick auf den Knopf soll
    // auch dann nichts nach oben durchreichen, wenn ein Aufrufer spaeter wieder einen anbringt.
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onAktion(aktion);
      }}
      className={`${ZEILEN_AKTION} inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-lg px-3 py-1.5 text-[12px] font-semibold motion-safe:transition-colors ${
        gefuellt
          ? "border border-transparent bg-primary text-on-primary hover:bg-[#a8380a]"
          : "border border-outline-variant text-primary hover:bg-primary-container/30 dark:border-outline/40 dark:hover:bg-surface-variant"
      } ${breit ? "w-full" : ""}`}
    >
      {aktion.text}
    </button>
  );
}

/**
 * Grund-Pille in der Liste (Antoine, 20.10.2026, Mockup V5): in „Du bist dran“ die EINE Pille unter
 * dem Namen — sie löst den Chip „Antwort vom Gast“ dort ab. In den übrigen Gruppen steht keine
 * Pille, dort bleibt der Chip wie bisher. Bei „Mail nicht zugestellt“ folgt die Zeile „Adresse · Grund“.
 */
function GrundPilleBlock({ anfrage, stand, onAntworten }: { anfrage: Inquiry; stand: AnfrageStand; onAntworten?: (anfrage: Inquiry) => void }) {
  const pille = grundPille({ stand, tatsachen: anfrage.tatsachen });
  const gastAm = ungeleseneGastAntwort(anfrage);
  // „Angebot wird erstellt“ steht in der Liste schon als Spinner in der Aktionsspalte — dieselbe
  // Aussage nicht zweimal in einer Zeile (die Kanban-Karte zeigt sie als Etikett).
  if (pille?.art === "generiert") return null;
  if (!pille) {
    return gastAm ? (
      <div className="mt-1">
        <GastAntwortChip am={gastAm} onKlick={onAntworten ? () => onAntworten(anfrage) : undefined} />
      </div>
    ) : null;
  }
  const zeile = pille.art === "zustellung" ? zustellZeile(anfrage.tatsachen) : null;
  return (
    <div className="mt-1 flex flex-col items-start gap-1">
      <GrundPilleInline pille={pille} onKlick={pille.art === "gast_antwort" && onAntworten ? () => onAntworten(anfrage) : undefined} />
      {zeile && (
        <span data-testid="karte-zustell-zeile" className="font-body-sm text-[11.5px] font-medium text-error">
          {zeile}
        </span>
      )}
    </div>
  );
}

interface ZeilenProps {
  zeilen: AnfrageZeile[];
  /**
   * Einheit E (`docs/PLAN-VORGANGSSEITE-UMBAU.md`): die Zeile bekommt eine ZIEL-ADRESSE, keinen
   * Klick-Rueckruf mehr. Vorher hiess dieser Prop `onZeile` und rief in `pages/inquiries.tsx` ein
   * `navigate(...)` auf — ein Ziel, das der Browser nie zu sehen bekam, weshalb Cmd-/Strg-/
   * Mittelklick und „Link in neuem Tab öffnen" nicht funktionierten. Jetzt rendert die Zeile einen
   * echten `<Link>` (`ZeilenLink`) auf genau diese Adresse.
   */
  zeilenZiel: (anfrage: Inquiry) => string;
  onAktion: (anfrage: Inquiry, aktion: StandAktion) => void;
  /** Antoine 19.10.2026: Klick auf „Antwort vom Gast" öffnet die Antwort-Ansicht (optional). */
  onAntworten?: (anfrage: Inquiry) => void;
}

interface TabellenProps extends ZeilenProps {
  sort: ListSortZustand<AnfrageZeile>;
}

/** Der zugaengliche Name des Zeilen-Links — eine Stelle, damit Tabelle und Karte gleich heissen. */
function zeilenLinkLabel(anfrage: Inquiry): string {
  return `Vorgang von ${anfrage.customerName} öffnen`;
}

/**
 * Breitbild-Fassung: Kunde · Kam rein · Stand · Wert · Aktion (§ 1, Punkt 5; Wert seit dem
 * Listenstandard, weil „Nach Wert“ sonst mit der Sortierauswahl weggefallen wäre).
 * Listenstandard (Brief § 2.4): eigener Scrollbereich, damit der Kopf `sticky` stehen bleibt —
 * an der Karte (`overflow-hidden`) scheitert `sticky` still.
 */
function GruppenTabelle({ zeilen, zeilenZiel, onAktion, onAntworten, sort }: TabellenProps) {
  return (
    <div className="hidden max-h-[max(20rem,calc(100vh-19rem))] overflow-auto md:block">
      <table className="handy-karten w-full min-w-[820px] border-collapse text-left">
        <thead>
          <tr>
            {ANFRAGEN_SPALTEN.map((sp) => (
              <SortierKopf
                key={sp.key}
                label={sp.label}
                aktiv={sort.sortKey === sp.key ? sort.dir : null}
                onClick={() => sort.toggle(sp.key)}
                rechts={sp.key === "wert"}
              />
            ))}
            {/* Brief § 2.2: reine Aktionsspalte ohne sichtbare Überschrift. */}
            <th className={LISTEN_KOPF_ZELLE}>
              <span className="sr-only">Aktion</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-outline-variant/20 dark:divide-outline/20">
          {zeilen.map(({ anfrage, stand }) => (
            // Einheit E: `relative` traegt den Overlay-Link (`ZeilenLink` in der ersten Zelle) —
            // er deckt die ganze Zeile ab, weil der `<tr>` sein naechster positionierter Vorfahre
            // ist. Zwischen `<tr>` und `<td>` darf kein `<a>` stehen, deshalb liegt der Link IN
            // der ersten Zelle und nicht um die Zeile.
            <tr
              key={anfrage.id}
              className="group relative motion-safe:transition-colors hover:bg-surface-container-high/30 dark:hover:bg-surface-variant/30"
            >
              <td className="table-cell align-top">
                <ZeilenLink to={zeilenZiel(anfrage)} label={zeilenLinkLabel(anfrage)} />
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-container-high text-xs font-bold text-secondary dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
                    {initialen(anfrage.customerName)}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-on-surface dark:text-inverse-on-surface">
                      {ungeleseneGastAntwort(anfrage) && <UngelesenPunkt />}
                      {anfrage.customerName}
                    </p>
                    {eckdatenZeile(anfrage) && (
                      <p className="mt-0.5 truncate font-body-sm text-xs text-on-surface-variant/80">
                        {eckdatenZeile(anfrage)}
                      </p>
                    )}
                    <GrundPilleBlock anfrage={anfrage} stand={stand} onAntworten={onAntworten} />
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <HerkunftPille herkunft={anfrage.herkunft} isTest={anfrage.isTest} anfrage={anfrage} />
                      <NotizZaehler anfrage={anfrage} />
                    </div>
                  </div>
                </div>
              </td>
              <td className="table-cell whitespace-nowrap align-top text-on-surface-variant">
                <div className="flex flex-col items-start gap-1">
                  <span>{timeAgo(anfrage.createdAt)}</span>
                  <AngebotVersendetBadge stand={stand} />
                </div>
              </td>
              <td className="table-cell max-w-[420px] align-top">
                <StandSatz stand={stand} />
              </td>
              <td className="table-cell whitespace-nowrap text-right align-top font-semibold tabular-nums text-on-surface dark:text-inverse-on-surface">
                {anfrage.tatsachen?.aLaCarte === true ? A_LA_CARTE_KLEIN : anfrage.amountTotalCents != null ? money(anfrage.amountTotalCents) : <KeineAngabe />}
              </td>
              <td className="table-cell align-top">
                <AktionsKnopf stand={stand} onAktion={(a) => onAktion(anfrage, a)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * P3.4 — Mobilfassung derselben Logik: Karten statt Tabelle, Stand-Satz und die eine Aktion
 * bleiben erhalten. Es ist DIESELBE Aufteilung und dieselbe Aktion, nur anders angeordnet —
 * kein zweiter Datenweg, keine zweite Regel.
 *
 * Bewusst OHNE eigenen Höhen-/Scrollcontainer: die Hausregel gegen die iOS-Toolbar-Lücke
 * (`height:100dvh` + `flex:1; overflow-y:auto` + `flex-shrink:0`) gilt Layouts mit einem am
 * unteren Rand klebenden Element. Diese Seite hat keins — `AppShell` scrollt das Dokument
 * (`min-h-screen`, `ui/AppShell.tsx`). Ein zweiter Scrollbereich INNERHALB eines scrollenden
 * Dokuments erzeugt genau die verschachtelte Scrollfalle, die die Regel verhindern soll.
 * Was die Regel verbietet, wird hier eingehalten: kein `position: fixed`, keine Spacer-Hacks,
 * keine `visualViewport`-Rechnerei.
 */
interface AnfrageKarteProps {
  anfrage: Inquiry;
  stand: AnfrageStand;
  /** Ziel-Adresse der Karte (Einheit E, s. `ZeilenProps.zeilenZiel`). */
  zeilenZiel: (anfrage: Inquiry) => string;
  onAktion: (anfrage: Inquiry, aktion: StandAktion) => void;
  onAntworten?: (anfrage: Inquiry) => void;
}

/**
 * Eine einzelne Karte — extrahiert aus `GruppenKarten` (P3.4), damit die Kanban-Ansicht
 * (`AnfragenKanban.tsx`) dieselbe Kartenoptik zeigt statt sie zweimal zu pflegen. Reine
 * Anordnung, keine zweite Formulierung: `StandSatz`/`AktionsKnopf`/`AngebotVersendetBadge`
 * bleiben unverändert dieselben Bausteine.
 */
export function AnfrageKarte({ anfrage, stand, zeilenZiel, onAktion, onAntworten }: AnfrageKarteProps) {
  return (
    // Einheit E: statt `role="button"`/`tabIndex`/`onClick`/`onKeyDown` (eine nachgebaute
    // Schaltflaeche ohne Linkziel) jetzt ein echter Overlay-Link. `data-testid` ist der stabile
    // Griff fuer die Tests, die die Karte vorher ueber `role="button"` gefunden haben.
    <div
      data-testid="anfrage-karte"
      className="group relative flex flex-col gap-2 px-[18px] py-3.5 motion-safe:transition-colors hover:bg-surface-container-high/30 dark:hover:bg-surface-variant/30"
    >
      <ZeilenLink to={zeilenZiel(anfrage)} label={zeilenLinkLabel(anfrage)} />
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-container-high text-xs font-bold text-secondary dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
          {initialen(anfrage.customerName)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-on-surface dark:text-inverse-on-surface">
            {ungeleseneGastAntwort(anfrage) && <UngelesenPunkt />}
            {anfrage.customerName}
          </p>
          {eckdatenZeile(anfrage) && (
            <p className="mt-0.5 truncate font-body-sm text-xs text-on-surface-variant/80">{eckdatenZeile(anfrage)}</p>
          )}
          <GrundPilleBlock anfrage={anfrage} stand={stand} onAntworten={onAntworten} />
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <HerkunftPille herkunft={anfrage.herkunft} isTest={anfrage.isTest} anfrage={anfrage} />
            <NotizZaehler anfrage={anfrage} />
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="whitespace-nowrap font-body-sm text-[11px] text-on-surface-variant/70">{timeAgo(anfrage.createdAt)}</span>
          <AngebotVersendetBadge stand={stand} />
        </div>
      </div>
      <StandSatz stand={stand} />
      <AktionsKnopf stand={stand} onAktion={(a) => onAktion(anfrage, a)} breit />
    </div>
  );
}

/** P3.4 — Mobilfassung der Tabelle: dieselbe Karte (`AnfrageKarte`), nur untereinander. */
function GruppenKarten({ zeilen, zeilenZiel, onAktion, onAntworten }: ZeilenProps) {
  return (
    <div className="flex flex-col divide-y divide-outline-variant/20 md:hidden dark:divide-outline/20">
      {zeilen.map(({ anfrage, stand }) => (
        <AnfrageKarte key={anfrage.id} anfrage={anfrage} stand={stand} zeilenZiel={zeilenZiel} onAktion={onAktion} onAntworten={onAntworten} />
      ))}
    </div>
  );
}

interface AbschnittProps extends TabellenProps {
  titel: string;
  /**
   * Die Aufschlüsselung unter der Überschrift (§ 12.5). Sie trägt die Zahl der Zeilen — deshalb
   * steht daneben KEIN zweiter Zähler mehr: zwei Zählungen derselben Menge sind genau der
   * Widerspruch, den 32a abschafft.
   */
  untertitel: string;
  /** Eingeklappt starten („Entschieden") — Antoines Festlegung „mit alle anzeigen". */
  eingeklappt: boolean;
}

function GruppenAbschnitt({ titel, untertitel, eingeklappt, zeilen, zeilenZiel, onAktion, onAntworten, sort }: AbschnittProps) {
  const [offen, setOffen] = useState(!eingeklappt);
  if (zeilen.length === 0) return null;

  return (
    /**
     * P10.5 (32a) — **jede Gruppe ist eine eigene Karte.** Vorher waren es Abschnitte in einer
     * gemeinsamen Karte, getrennt nur durch eine Linie; der Entwurf setzt sie bewusst
     * auseinander, weil „wer ist dran" die Ordnung der Liste IST (§ 12.2) und keine Unterteilung
     * innerhalb einer Tabelle. Der Kartenrahmen ist derselbe wie überall (`.card`) — es entsteht
     * hier kein zweites Kartenaussehen.
     */
    <Card as="section" className="overflow-hidden">
      <div className="flex items-center gap-2 px-[18px] py-3">
        <div className="min-w-0">
          <h3 className="text-[13.5px] font-semibold text-on-background dark:text-inverse-on-surface">{titel}</h3>
          <p className="mt-0.5 font-body-sm text-[11.5px] text-on-surface-variant/80">{untertitel}</p>
        </div>
        {eingeklappt && (
          <button
            type="button"
            onClick={() => setOffen((v) => !v)}
            aria-expanded={offen}
            className="ml-auto inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:underline"
          >
            {offen ? "einklappen" : "alle anzeigen"}
            <Icon name={offen ? "expand_less" : "expand_more"} className="text-[16px]" />
          </button>
        )}
      </div>
      {offen && (
        <>
          <GruppenTabelle zeilen={zeilen} zeilenZiel={zeilenZiel} onAktion={onAktion} onAntworten={onAntworten} sort={sort} />
          <GruppenKarten zeilen={zeilen} zeilenZiel={zeilenZiel} onAktion={onAktion} onAntworten={onAntworten} />
        </>
      )}
    </Card>
  );
}

/** Zeilen ohne ausgelieferten Stand: gezeigt, aber nichts über sie behauptet. */
function OhneStandAbschnitt({
  anfragen,
  zeilenZiel,
}: {
  anfragen: Inquiry[];
  zeilenZiel: (anfrage: Inquiry) => string;
}) {
  if (anfragen.length === 0) return null;
  return (
    <Card as="section" className="overflow-hidden">
      <div className="flex flex-col gap-1 px-[18px] py-3">
        <div className="flex items-center gap-2">
          <h3 className="text-[13.5px] font-semibold text-on-background dark:text-inverse-on-surface">
            Stand noch nicht ermittelt
          </h3>
          <span className="rounded-full bg-[#F4EEE1] px-[7px] py-0.5 text-[10.5px] font-semibold text-[#6F675A] dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
            {anfragen.length}
          </span>
        </div>
        <p className="font-body-sm text-[11.5px] leading-relaxed text-on-surface-variant/80">
          Die Anfragenliste liefert für diese Zeilen die Tatsachen noch nicht mit. Hier steht deshalb
          kein Satz — eine nicht ausgelieferte Tatsache ist keine beobachtete Abwesenheit.
        </p>
      </div>
      <div className="flex flex-col divide-y divide-outline-variant/20 dark:divide-outline/20">
        {anfragen.map((anfrage) => (
          // Einheit E: derselbe Umbau wie bei `AnfrageKarte` — echter Overlay-Link statt
          // nachgebauter Schaltflaeche.
          <div
            key={anfrage.id}
            data-testid="anfrage-zeile-ohne-stand"
            className="group relative flex items-start gap-3 px-[18px] py-3 motion-safe:transition-colors hover:bg-surface-container-high/30 dark:hover:bg-surface-variant/30"
          >
            <ZeilenLink to={zeilenZiel(anfrage)} label={zeilenLinkLabel(anfrage)} />
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-container-high text-xs font-bold text-secondary dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
              {initialen(anfrage.customerName)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-on-surface dark:text-inverse-on-surface">
                {anfrage.customerName}
              </p>
              {eckdatenZeile(anfrage) && (
                <p className="mt-0.5 truncate font-body-sm text-xs text-on-surface-variant/80">
                  {eckdatenZeile(anfrage)}
                </p>
              )}
              <div className="mt-1">
                <HerkunftPille herkunft={anfrage.herkunft} isTest={anfrage.isTest} anfrage={anfrage} />
              </div>
            </div>
            <span className="shrink-0 whitespace-nowrap font-body-sm text-[11px] text-on-surface-variant/70">
              {timeAgo(anfrage.createdAt)}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export interface AnfragenGruppenAnsichtProps {
  anfragen: Inquiry[];
  /** Ziel-Adresse der Zeile (Einheit E) — die Zeile ist ein echter Link dorthin, kein `onClick`. */
  zeilenZiel: (anfrage: Inquiry) => string;
  /** Klick auf die eine Aktion — führt zu der Stelle, die es schon gibt (`aktion.ziel`). */
  onAktion: (anfrage: Inquiry, aktion: StandAktion) => void;
  /** Antoine 19.10.2026: „Antwort vom Gast" angeklickt → Antwort-Ansicht öffnen. */
  onAntworten?: (anfrage: Inquiry) => void;
  /** Wie viele Anfragen es ohne Suche/Filter gibt — für „n von N Anfragen“ in der Fußzeile. */
  gesamt?: number;
}

export function AnfragenGruppenAnsicht({ anfragen, zeilenZiel, onAktion, onAntworten, gesamt = anfragen.length }: AnfragenGruppenAnsichtProps) {
  const { gruppen, ohneStand, wartetAufDich } = teileInGruppen(anfragen);
  const sort = useAnfragenSortierung();
  const wertSumme = anfragen.reduce((s, a) => s + (a.amountTotalCents ?? 0), 0);

  return (
    // P10.5: Karten untereinander statt Abschnitte in einer Karte — der Abstand ersetzt die Linie.
    <div className="flex flex-col gap-4">
      <Card className="px-[18px] py-3.5">
        <p className="text-[13.5px] text-on-surface-variant dark:text-secondary-fixed-dim">
          {anfragen.length} {anfragen.length === 1 ? "Anfrage" : "Anfragen"}.{" "}
          <strong className="font-semibold text-on-background dark:text-inverse-on-surface">
            {wartetAufDich === 0
              ? "Nichts wartet gerade auf dich."
              : `Bei ${wartetAufDich} wartet etwas auf dich.`}
          </strong>
        </p>
      </Card>

      {anfragen.length === 0 ? (
        <Card className="overflow-hidden">
          <EmptyState
            icon="inbox"
            title="Keine Anfragen"
            hint="Sobald über Ihr Anfrageformular oder per E-Mail eine Anfrage eingeht, erscheint sie automatisch hier."
          />
        </Card>
      ) : (
        <>
          {fasseFuerAnzeigeZusammen(gruppen).map(({ gruppe, titel, untertitel, zeilen }) => (
            <GruppenAbschnitt
              key={gruppe}
              titel={titel}
              untertitel={untertitel}
              eingeklappt={EINGEKLAPPT.has(gruppe)}
              zeilen={sort.sortiere(zeilen)}
              zeilenZiel={zeilenZiel}
              onAktion={onAktion}
              onAntworten={onAntworten}
              sort={sort}
            />
          ))}
          <OhneStandAbschnitt anfragen={ohneStand} zeilenZiel={zeilenZiel} />
          {/* Brief § 2.5: Anzahl · Summe der sichtbaren Werte · Sortierung mit „Zurücksetzen“. */}
          <Card className="overflow-hidden">
            <ListFooter
              anzahl={anfragen.length}
              gesamt={gesamt}
              einheit={gesamt === 1 ? "Anfrage" : "Anfragen"}
              summe={money(wertSumme)}
              sortiertNach={sort.spalte?.label ?? null}
              richtung={sort.dir}
              onReset={sort.reset}
            />
          </Card>
        </>
      )}
    </div>
  );
}
